/* [ START OF MODULE - VER 3.3 (src/context/RealtimeContext.jsx) ] */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '../supabaseClient';
import { RealtimeContext } from './useRealtime';

// ─────────────────────────────────────────────────────────────────────────────
// BUILD-2026.305 / RMI-2026-133 — ONE CHANNEL PER TABLE
//
// Root cause of the dead ticker (proven via clean-room isolation in the live
// browser): the previous design (VER 2.x) stacked all 7 postgres_changes
// bindings onto a SINGLE channel. Supabase Realtime silently fails to deliver
// postgres_changes events when multiple table bindings share one channel — the
// channel connects and reports SUBSCRIBED, but no change events ever flow.
//
// Controlled tests that settled it (each a from-scratch supabase-js client):
//   1 table  on 1 channel   → event delivered
//   7 tables on 1 channel    → ZERO events (this was the bug)
//   7 tables on 7 channels   → events delivered
//
// This explains "worked once, degraded over time": tables were added to the
// single shared channel incrementally over months. Below some count it worked;
// at 7 bindings it crossed the threshold where delivery silently stopped.
//
// THE FIX: create one dedicated channel per watched table. Each table's
// postgres_changes binding lives alone on its own channel, which is the
// reliable Supabase pattern. The public API (subscribe(table, fn) + status)
// is unchanged, so no consumer needs to change.
//
// ALSO REMOVED: the manual supabase.realtime.disconnect()/connect() transport
// cycling from VER 2.1. The clean-room never used it and worked fine; on a
// multi-channel client it would disrupt every channel at once. Supabase's
// client manages the socket transport itself — we let it. Reconnection is
// handled by re-subscribing the channels (see reconnectAll), which the client
// transparently multiplexes over its managed socket.
// ─────────────────────────────────────────────────────────────────────────────

const WATCHED_TABLES = [
  'work_orders', 'service_requests', 'client_messages',
  'invoices', 'tecc_payments', 'contractor_invoices', 'assets', 'system_activity_logs',
];

const WATCHDOG_INTERVAL_MS = 45000; // 45s — re-subscribe if a channel goes quiet+errored
const MAX_RETRY_DELAY_MS    = 30000;
// RMI-2026-331: only run the full teardown/rebuild if the PWA was actually
// backgrounded long enough to risk a stale socket. Below this, a quick
// app-switch (checking another app, notification shade, etc.) went straight
// back to 'visible' and didn't need channel recovery — rebuilding anyway was
// producing a visible "refresh" flash on every glance away.
const VISIBILITY_RECOVERY_THRESHOLD_MS = 15000;

export const RealtimeProvider = ({ children }) => {
  const [status, setStatus]   = useState('CONNECTING');
  const listenersRef           = useRef({});
  const isMountedRef           = useRef(true);
  // Map of table -> channel instance. One channel per table.
  const channelsRef            = useRef({});
  // Map of table -> last subscribe status string ('SUBSCRIBED' | 'CLOSED' | ...)
  const channelStatusRef       = useRef({});
  const retryTimerRef          = useRef(null);
  const watchdogRef            = useRef(null);
  const retryCountRef          = useRef(0);
  const isConnectingRef        = useRef(false); // guard against concurrent rebuilds
  const hiddenAtRef            = useRef(null);   // RMI-2026-331: timestamp when tab last went hidden

  const subscribe = useCallback((table, fn) => {
    if (!listenersRef.current[table]) listenersRef.current[table] = new Set();
    listenersRef.current[table].add(fn);
    return () => listenersRef.current[table]?.delete(fn);
  }, []);

  const dispatch = useCallback((table, event, payload) => {
    listenersRef.current[table]?.forEach(fn => {
      try { fn(event, payload); } catch (e) {
        console.error(`[Realtime] Listener error on ${table}:`, e);
      }
    });
  }, []);

  const clearTimers = useCallback(() => {
    clearTimeout(retryTimerRef.current);
    clearInterval(watchdogRef.current);
    retryTimerRef.current = null;
    watchdogRef.current   = null;
  }, []);

  // Recompute the aggregate status from the per-table channel statuses.
  // ACTIVE   = every channel SUBSCRIBED (all tables live)
  // DEGRADED = some channels up, some down (partial connectivity)
  // OFFLINE  = every channel closed/errored/timed out
  // CONNECTING = still settling / no data yet
  const recomputeStatus = useCallback(() => {
    if (!isMountedRef.current) return;
    const statuses = Object.values(channelStatusRef.current);
    if (statuses.length === 0) { setStatus('CONNECTING'); return; }
    const allSubscribed = statuses.every(s => s === 'SUBSCRIBED');
    const allDown = statuses.every(s => s === 'CLOSED' || s === 'CHANNEL_ERROR' || s === 'TIMED_OUT');
    if (allSubscribed) setStatus('ACTIVE');
    else if (allDown) setStatus('OFFLINE');
    else setStatus('DEGRADED');
  }, []);

  // Tear down every channel.
  const teardownAll = useCallback(async () => {
    const channels = Object.values(channelsRef.current);
    channelsRef.current = {};
    channelStatusRef.current = {};
    await Promise.all(channels.map(async (ch) => {
      try { await supabase.removeChannel(ch); } catch (_) { console.warn('[Realtime] removeChannel error:', _); }
    }));
  }, []);

  // Build one channel per watched table.
  const buildChannels = useCallback(async () => {
    if (isConnectingRef.current) {
      console.log('[Realtime] buildChannels already in progress — skipping.');
      return;
    }
    isConnectingRef.current = true;

    try {
      await teardownAll();
      clearTimers();
      if (!isMountedRef.current) { isConnectingRef.current = false; return; }
      setStatus('CONNECTING');

      // Unique suffix per build so a stale server-side channel name never
      // collides with the fresh one after a reconnect.
      const salt = Math.random().toString(36).substring(2, 8);

      WATCHED_TABLES.forEach(table => {
        const ch = supabase
          .channel(`tecc_${table}_${salt}`)
          .on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
            if (!isMountedRef.current) return;
            dispatch(table, payload.eventType, payload);
          })
          .subscribe((subStatus, err) => {
            if (!isMountedRef.current) return;
            channelStatusRef.current[table] = subStatus;

            if (subStatus === 'SUBSCRIBED') {
              retryCountRef.current = 0;
            } else if (subStatus === 'CLOSED' || subStatus === 'CHANNEL_ERROR' || subStatus === 'TIMED_OUT') {
              console.warn(`[Realtime] Channel for ${table} dropped:`, subStatus, err?.message || '');
              // Schedule a full rebuild (debounced via isConnectingRef + backoff).
              if (!retryTimerRef.current) {
                const delay = Math.min(2000 * Math.pow(2, retryCountRef.current), MAX_RETRY_DELAY_MS);
                retryCountRef.current += 1;
                retryTimerRef.current = setTimeout(async () => {
                  retryTimerRef.current = null;
                  if (isMountedRef.current) await buildChannels();
                }, delay);
              }
            }
            recomputeStatus();
          });

        channelsRef.current[table] = ch;
      });

      isConnectingRef.current = false;

      // Watchdog: if every channel is down for a sustained period, rebuild.
      // (No heartbeat needed — supabase-js sends its own socket heartbeats and
      // manages the transport. We only need to catch the case where channels
      // errored and the scheduled retry didn't recover them.)
      watchdogRef.current = setInterval(async () => {
        if (!isMountedRef.current) return;
        const statuses = Object.values(channelStatusRef.current);
        const allDown = statuses.length > 0 &&
          statuses.every(s => s === 'CLOSED' || s === 'CHANNEL_ERROR' || s === 'TIMED_OUT');
        if (allDown && !retryTimerRef.current && !isConnectingRef.current) {
          console.warn('[Realtime] Watchdog: all channels down — rebuilding.');
          await buildChannels();
        }
      }, WATCHDOG_INTERVAL_MS);

    } catch (err) {
      isConnectingRef.current = false;
      console.error('[Realtime] buildChannels threw:', err.message);
      const delay = Math.min(2000 * Math.pow(2, retryCountRef.current), MAX_RETRY_DELAY_MS);
      retryCountRef.current += 1;
      if (!retryTimerRef.current) {
        retryTimerRef.current = setTimeout(async () => {
          retryTimerRef.current = null;
          if (isMountedRef.current) await buildChannels();
        }, delay);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable — all mutable state lives in refs

  useEffect(() => {
    isMountedRef.current = true;
    buildChannels();

    // On visibility restore (phone unlock, tab refocus) and network recovery,
    // rebuild all channels. supabase-js manages the socket, but a long sleep
    // can leave channels in a stale state; a clean re-subscribe is the safe
    // recovery. retryCount reset so backoff starts fresh.
    //
    // RMI-2026-331: gated behind VISIBILITY_RECOVERY_THRESHOLD_MS. A brief
    // app-switch (checking another app, notification shade) went hidden then
    // straight back to visible in a second or two — the socket was never at
    // risk, so rebuilding all 8 channels every time was pure overhead and the
    // visible cause of an apparent "refresh" on every glance away. Only a
    // genuinely stale sleep (phone lock, long backgrounding) triggers rebuild.
    const handleVisibility = async () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;
      if (!isMountedRef.current) return;
      const elapsed = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : Infinity;
      if (elapsed < VISIBILITY_RECOVERY_THRESHOLD_MS) return;
      retryCountRef.current = 0;
      await buildChannels();
    };
    const handleOnline = async () => {
      if (!isMountedRef.current) return;
      console.log('[Realtime] Network online — rebuilding channels.');
      retryCountRef.current = 0;
      await buildChannels();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);

    return () => {
      isMountedRef.current = false;
      isConnectingRef.current = false;
      clearTimers();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      teardownAll();
    };
  }, [buildChannels, clearTimers, teardownAll]);

  return (
    <RealtimeContext.Provider value={{ subscribe, status }}>
      {children}
    </RealtimeContext.Provider>
  );
};

/*
=========================================
CHANGE MANIFEST
=========================================
VER 3.3 — 2026-10-02 — BUILD-2026.1041 — RMI-2026-660 — drop the visibility-restored logs
- Removed the two console.log lines in handleVisibility ("Visibility restored after Nms —
  below threshold" fired on every glance away from the app; "Visibility restored — rebuilding
  channels"). The threshold logic, rebuild and retry reset are unchanged. Kept: the
  "already in progress" and "Network online" logs and every console.warn / console.error
  (dropped channel, watchdog rebuild, listener/build errors). Manifest trimmed to the newest
  five entries; the three oldest archived in docs/INLINE_CHANGELOG_ARCHIVE_3.md.
=========================================
VER 3.2 — 2026-07-15 — BUILD-2026.589 — RMI-2026-331 — GATE VISIBILITY REBUILD BEHIND A TIME THRESHOLD
- SYMPTOM: mobile PWA appeared to "refresh" every time the user left it to
  check another app, even for a couple of seconds.
- ROOT CAUSE: handleVisibility rebuilt ALL 8 realtime channels (teardown +
  resubscribe) on every single visibilitychange -> visible event, with no
  check on how long the tab was actually hidden. Combined with a similar
  unconditional refetch in useMobileSession.js (RMI-2026-331 companion fix),
  a 2-second app-switch triggered the same full recovery as an hour-long
  backgrounding — producing a visible connection-status flicker + re-render.
- FIX: added hiddenAtRef, stamped on the 'hidden' transition. On 'visible',
  only rebuild if elapsed since hidden >= VISIBILITY_RECOVERY_THRESHOLD_MS
  (15s). Quick glances now leave the existing channels running untouched;
  genuine long sleeps (phone lock, backgrounding) still trigger full recovery.
- No change to the online-event rebuild, per-channel error retry, or the 45s
  watchdog — all separate recovery paths, all still unconditional (correctly
  so: those only fire on evidence of an actual dropped connection).
=========================================
VER 3.0 — 2026-05-28 — BUILD-2026.305 — RMI-2026-133 — ONE CHANNEL PER TABLE
- ROOT CAUSE (proven by clean-room isolation in the live browser):
  The single shared channel carrying all 7 postgres_changes bindings was the
  bug. Supabase Realtime silently stops delivering postgres_changes when
  multiple table bindings are stacked on one channel — it reports SUBSCRIBED
  but no events arrive. Controlled tests:
    1 table  / 1 channel   → delivered
    7 tables / 1 channel    → ZERO delivery (the bug)
    7 tables / 7 channels   → delivered
  Explains "worked once, degraded over time": tables were added to the single
  channel incrementally; at 7 bindings delivery silently broke.
- FIX: one dedicated channel per watched table (tecc_<table>_<salt>). Each
  postgres_changes binding lives alone on its own channel. Public API
  (subscribe(table, fn) + status) unchanged — no consumer edits required.
- REMOVED: manual supabase.realtime.disconnect()/connect() transport cycling
  (VER 2.1). Clean-room proved it unnecessary; on a multi-channel client it
  would disrupt all channels at once. supabase-js manages its own socket
  transport + heartbeats. We removed our 25s broadcast heartbeat too — it was
  only there to keep the single shared channel warm.
- RECONNECTION: preserved. Per-channel CLOSED/CHANNEL_ERROR schedules a
  debounced exponential-backoff full rebuild; a 45s watchdog rebuilds if every
  channel is simultaneously down; visibilitychange + online events trigger a
  fresh rebuild after phone unlock / network recovery.
- STATUS: aggregate status derived from per-channel statuses — ACTIVE only if ALL
  channels are SUBSCRIBED, DEGRADED if some are down, OFFLINE if all are down,
  CONNECTING while settling.
=========================================
VER 3.1 — 2026-06-15 — BUILD-2026.447 — RECOMPUTE ALL-CHANNEL GATE
- FIX: `recomputeStatus` now requires ALL channels to be SUBSCRIBED before
  declaring ACTIVE (LIVE). Previously any single subscribed channel would show
  LIVE, which was misleading — one table's channel could be down while the
  ticker reported full health.
- ADDED: `DEGRADED` status — set when some channels are up and some are down.
  SystemLogTicker already had visual handling for DEGRADED (warning color),
  but recomputeStatus never emitted it. Now it does.
=========================================
VER 2.2 — May 15, 2026
- BUGFIX: Heartbeat send success resets lastEventRef so the 40s watchdog clock
  stays alive on idle channels. (Superseded by VER 3.0 — heartbeat removed.)
=========================================
*/
/* [ END OF MODULE - VER 3.3 (src/context/RealtimeContext.jsx) ] */
